import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core'
import { IxAvatar, IxButton, IxDropdownItem, ToastService } from '@siemens/ix-angular/standalone'
import { TranslatePipe } from '@ngx-translate/core'
import { sha256 } from 'js-sha256'
import { AuthService } from './auth.service'
import { LoginExpirationWarningComponent } from './login-expiration-warning/login-expiration-warning.component'

@Component({
  selector: 'user-auth',
  imports: [IxButton, IxAvatar, IxDropdownItem, TranslatePipe, LoginExpirationWarningComponent],
  providers: [ToastService],
  styles: [':host { display: contents; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isLoggingIn() !== true) {
      <ix-button icon="log-in" (click)="onLoginClicked()">{{ 'LOGIN.LOGIN_BUTTON' | translate }}</ix-button>
    } @else {
      @let expirationTime = this.authService.expirationDateTime();
      @if (expirationTime) {
        <login-expiration-warning [expirationTime]="expirationTime" (logout)="authService.logout()" />
      }
      <ix-avatar [username]="userName()" [extra]="userEmail()" [initials]="initials()" [image]="gravatar()">
        <ix-dropdown-item icon="log-out" [label]="'LOGIN.LOGOUT_BUTTON' | translate" (click)="authService.logout()" />
      </ix-avatar>
    }
  `,
})
export class UserAuthComponent {
  authService = inject(AuthService)

  isLoggingIn() {
    return this.authService.isAuthenticated()
  }

  readonly userName = computed(() => {
    const givenName = this.authService.userInfo()?.given_name ?? ''
    const familyName = this.authService.userInfo()?.family_name ?? ''
    return `${familyName}, ${givenName}`
  })

  readonly initials = computed(() => {
    const givenInitial = (this.authService.userInfo()?.given_name ?? ' ')[0]
    const familyInitial = (this.authService.userInfo()?.family_name ?? ' ')[0]
    return givenInitial + familyInitial
  })

  readonly userEmail = computed(() => this.authService.userInfo()?.email ?? '')

  /**
   * Gravatar URL for the user's email address. Fallback to generic image if no email is set or the user has no gravatar.
   */
  readonly gravatar = computed(() => {
    const email = this.authService.userInfo()?.email.trim().toLowerCase() ?? ''
    const hash = sha256(email)
    return `https://www.gravatar.com/avatar/${hash}?d=mp`
  })

  onLoginClicked() {
    this.authService.login()
  }
}
